import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './ActivityModal.css';

const LAST_SHOWN_KEY = 'activityModal_lastShown';
const CHECK_INTERVAL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

const ActivityModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activityData, setActivityData] = useState(null);
  const [featuredMembers, setFeaturedMembers] = useState([]);
  const [hofCategories, setHofCategories] = useState({});
  const [indieOfDay, setIndieOfDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const leftColRef = useRef(null);
  const [indieHeight, setIndieHeight] = useState(null);
  const navigate = useNavigate();
  const MIN_INDIE_HEIGHT = 400;

  // Helper to get media URL for indie game media items
  const getIndieMediaUrl = (mediaItem) => {
    if (!mediaItem) return null;
    if (mediaItem.url) return mediaItem.url;
    if (mediaItem.fileId) return `/api/media/${mediaItem.fileId}`;
    return null;
  };

  useEffect(() => {
    checkAndShowModal();
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);

    // Measure left column height and update indie card height
    const measure = () => {
      try {
        if (leftColRef.current) setIndieHeight(Math.max(leftColRef.current.offsetHeight, MIN_INDIE_HEIGHT));
      } catch (e) {
        // ignore
      }
    };
    measure();
    window.addEventListener('resize', measure);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('resize', measure);
    };
  }, []);

  const checkAndShowModal = async () => {
    try {
      const lastShown = localStorage.getItem(LAST_SHOWN_KEY);
      const now = Date.now();

      // Check if 24 hours have passed since last shown
      if (!lastShown || now - parseInt(lastShown) >= CHECK_INTERVAL) {
        // Fetch activity counts
        const response = await axios.get('/api/activity/new-counts');
        const data = response.data.data;

        // Fetch Hall of Fame to show featured legends
        try {
          const hofResp = await axios.get('/api/hall-of-fame');
          const members = hofResp.data.data || [];
          const categories = hofResp.data.categories || {};
          // Keep only featured members and limit to first 6 for the modal
          const featured = (members.filter(m => m.featured).slice(0, 6));
          setFeaturedMembers(featured);
          setHofCategories(categories);
        } catch (err) {
          console.error('Error fetching Hall of Fame for activity modal:', err);
        }

        // Fetch Indie Games and pick a random one for shoutout
        try {
          const indieResp = await axios.get('/api/indie-games?page=1&limit=96');
          const games = indieResp.data?.data || [];
          if (games.length > 0) {
            const rand = Math.floor(Math.random() * games.length);
            setIndieOfDay(games[rand]);
          }
        } catch (err) {
          console.error('Error fetching indie games for modal:', err);
        }

        // Show modal every 24 hours regardless of whether there's new activity
        setActivityData(data);
        setIsOpen(true);
      }
    } catch (error) {
      console.error('Error fetching activity counts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Save current timestamp to localStorage
    localStorage.setItem(LAST_SHOWN_KEY, Date.now().toString());
    setIsOpen(false);
  };

  const handleNavigate = (path) => {
    handleClose();
    navigate(path);
  };

  if (!isOpen) {
    return null;
  }

  const activityItems = [
    {
      count: activityData.communityCards,
      label: 'Community Card',
      labelPlural: 'Community Cards',
      icon: '👥',
      path: '/community',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      count: activityData.giveaways,
      label: 'Giveaway',
      labelPlural: 'Giveaways',
      icon: '🎁',
      path: '/giveaways',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      count: activityData.discounts,
      label: 'Discount',
      labelPlural: 'Discounts',
      icon: '💰',
      path: '/discounts',
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      count: activityData.indieGames,
      label: 'Indie Game',
      labelPlural: 'Indie Games',
      icon: '🎮',
      path: '/indie-games',
      gradient: 'from-orange-500 to-red-500'
    },
    {
      count: activityData.blogs,
      label: 'Blog Post',
      labelPlural: 'Blog Posts',
      icon: '📝',
      path: '/blog',
      gradient: 'from-indigo-500 to-purple-500'
    },
    {
      count: activityData.upcomingGames,
      label: 'Upcoming Game',
      labelPlural: 'Upcoming Games',
      icon: '🚀',
      path: '/upcoming-releases',
      gradient: 'from-yellow-500 to-orange-500'
    },
    {
      count: activityData.codeRequests,
      label: 'Code Request',
      labelPlural: 'Code Requests',
      icon: '🔑',
      path: '/code-requests',
      gradient: 'from-teal-500 to-cyan-500'
    }
  ].filter(item => item.count > 0); // Only show items with activity

  return (
    <div className="activity-modal-overlay" onClick={handleClose}>
      <div className="activity-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="activity-modal-header">
          <div className="activity-modal-title">
            <span className="activity-icon">✨</span>
            <h2>What's New in the Past 24 Hours</h2>
          </div>
          <button className="activity-modal-close" onClick={handleClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="activity-modal-body">
          <div className="activity-intro">
            <p>Check out the latest activity on GamersUnion! Here's what happened while you were away:</p>
          </div>

          {/* Featured Legends (from Hall of Fame) - show before other objects */}
          {featuredMembers && featuredMembers.length > 0 && (
            <div style={{ marginBottom: '1.25rem' }}>
              {/* Headings moved into the table below so they align side-by-side */}

              {windowWidth < 820 ? (
                <div>
                      <div ref={leftColRef} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {featuredMembers.map(member => {
                      const user = member.user;
                      const card = member.communityCard || {};
                      return (
                        <div
                          key={member._id}
                          onClick={() => handleNavigate(`/profile/${user.username}`)}
                          style={{
                            display: 'flex',
                            gap: '10px',
                            alignItems: 'center',
                            background: 'rgba(255,255,255,0.03)',
                            padding: '10px 14px',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            border: '1px solid rgba(255,255,255,0.06)',
                            width: '100%',
                            minHeight: '96px'
                          }}
                        >
                          <div style={{ width: 48, height: 48, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, border: '2px solid rgba(255,255,255,0.08)' }}>
                            {(card.avatar || user.avatar?.url) ? (
                              <img src={card.avatar || user.avatar?.url} alt={user.username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', fontWeight: 'bold' }}>{user.username[0].toUpperCase()}</div>
                            )}
                          </div>
                          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <div style={{ fontWeight: '700', fontSize: '1rem', color: 'transparent', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', backgroundClip: 'text' }}>{user.username}</div>
                            <div style={{ display: 'flex', gap: '6px', marginTop: 6, flexWrap: 'wrap' }}>
                              {member.categories && member.categories.map(cat => (
                                <span key={cat} style={{ background: 'rgba(255,255,255,0.06)', color: '#fff', padding: '4px 8px', borderRadius: '12px', fontSize: '0.8rem' }}>
                                  {hofCategories[cat]?.icon} {hofCategories[cat]?.name}
                                </span>
                              ))}
                            </div>

                            {card.socialLinks && card.socialLinks.length > 0 && (
                              <div style={{ display: 'flex', gap: '6px', marginTop: 8, flexWrap: 'wrap' }}>
                                {card.socialLinks.slice(0, 3).map((link, i) => (
                                  <a
                                    key={i}
                                    href={link.url}
                                    onClick={(e) => { e.stopPropagation(); }}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      background: 'rgba(255,255,255,0.04)',
                                      color: '#fff',
                                      padding: '4px 8px',
                                      borderRadius: '10px',
                                      fontSize: '0.8rem',
                                      textDecoration: 'none',
                                      border: '1px solid rgba(255,255,255,0.05)'
                                    }}
                                  >
                                    🔗 {link.platform}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {indieOfDay && (
                    <div style={{ marginTop: '12px' }}>
                      <h3 style={{ margin: '0 0 8px 0', color: '#FFD700' }}>🎮 Indie Game of the Day</h3>
                      <div style={{ width: '100%', borderRadius: 12, overflow: 'hidden', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)', cursor: 'pointer', display: 'flex', flexDirection: 'column' }} onClick={() => handleNavigate(`/indie-games/${indieOfDay._id}`)}>
                        <div style={{ height: 200, backgroundSize: 'cover', backgroundPosition: 'center', backgroundImage: indieOfDay.media && indieOfDay.media.length > 0 ? `url(${getIndieMediaUrl(indieOfDay.media[0])})` : 'linear-gradient(135deg,#667eea,#764ba2)' }} />
                        <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                          <div>
                            <div style={{ fontSize: '16px', fontWeight: 800, color: 'white', marginBottom: 6 }}>{indieOfDay.name}</div>
                            {indieOfDay.description && (
                              <p style={{ margin: 0, color: 'rgba(255,255,255,0.8)', fontSize: 13, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{indieOfDay.description}</p>
                            )}
                          </div>
                          <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
                            <div style={{ background: 'rgba(255,255,255,0.06)', padding: '6px 10px', borderRadius: 10, fontSize: 13 }}>👤 {indieOfDay.userId?.username || 'Unknown'}</div>
                            <div style={{ marginLeft: 'auto', color: '#64C8FF', fontWeight: 700 }}>View</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '16px' }}>
                  <tbody>
                    <tr>
                      <td style={{ verticalAlign: 'top', paddingRight: '16px' }}>
                        <h3 style={{ margin: 0, color: '#FFD700' }}>⭐ Featured Legends</h3>
                      </td>
                      <td style={{ verticalAlign: 'top', width: '360px' }}>
                        {indieOfDay && <h3 style={{ margin: 0, color: '#FFD700' }}>🎮 Indie Game of the Day</h3>}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ verticalAlign: 'top', width: 'calc(100% - 360px)', paddingRight: '16px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {featuredMembers.map(member => {
                            const user = member.user;
                            const card = member.communityCard || {};
                            return (
                              <div
                                key={member._id}
                                onClick={() => handleNavigate(`/profile/${user.username}`)}
                                style={{
                                  display: 'flex',
                                  gap: '10px',
                                  alignItems: 'center',
                                  background: 'rgba(255,255,255,0.03)',
                                  padding: '10px 14px',
                                  borderRadius: '12px',
                                  cursor: 'pointer',
                                  border: '1px solid rgba(255,255,255,0.06)',
                                  width: '100%',
                                  minHeight: '96px'
                                }}
                              >
                                <div style={{ width: 48, height: 48, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, border: '2px solid rgba(255,255,255,0.08)' }}>
                                  {(card.avatar || user.avatar?.url) ? (
                                    <img src={card.avatar || user.avatar?.url} alt={user.username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  ) : (
                                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', fontWeight: 'bold' }}>{user.username[0].toUpperCase()}</div>
                                  )}
                                </div>
                                <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                  <div style={{ fontWeight: '700', fontSize: '1rem', color: 'transparent', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', backgroundClip: 'text' }}>{user.username}</div>
                                  <div style={{ display: 'flex', gap: '6px', marginTop: 6, flexWrap: 'wrap' }}>
                                    {member.categories && member.categories.map(cat => (
                                      <span key={cat} style={{ background: 'rgba(255,255,255,0.06)', color: '#fff', padding: '4px 8px', borderRadius: '12px', fontSize: '0.8rem' }}>
                                        {hofCategories[cat]?.icon} {hofCategories[cat]?.name}
                                      </span>
                                    ))}
                                  </div>

                                  {card.socialLinks && card.socialLinks.length > 0 && (
                                    <div style={{ display: 'flex', gap: '6px', marginTop: 8, flexWrap: 'wrap' }}>
                                      {card.socialLinks.slice(0, 3).map((link, i) => (
                                        <a
                                          key={i}
                                          href={link.url}
                                          onClick={(e) => { e.stopPropagation(); }}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            background: 'rgba(255,255,255,0.04)',
                                            color: '#fff',
                                            padding: '4px 8px',
                                            borderRadius: '10px',
                                            fontSize: '0.8rem',
                                            textDecoration: 'none',
                                            border: '1px solid rgba(255,255,255,0.05)'
                                          }}
                                        >
                                          🔗 {link.platform}
                                        </a>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                      <td style={{ verticalAlign: 'top', width: '360px' }}>
                        {indieOfDay && (
                          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: indieHeight ? indieHeight + 'px' : '100%' }} onClick={() => handleNavigate(`/indie-games/${indieOfDay._id}`)}>
                              <div style={{ width: '100%', borderRadius: 12, overflow: 'hidden', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)', cursor: 'pointer', display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <div style={{ height: 200, backgroundSize: 'cover', backgroundPosition: 'center', backgroundImage: indieOfDay.media && indieOfDay.media.length > 0 ? `url(${getIndieMediaUrl(indieOfDay.media[0])})` : 'linear-gradient(135deg,#667eea,#764ba2)' }} />
                                <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', flex: 1, justifyContent: 'space-between' }}>
                                  <div>
                                    <div style={{ fontSize: '16px', fontWeight: 800, color: 'white', marginBottom: 6 }}>{indieOfDay.name}</div>
                                    {indieOfDay.description && (
                                      <p style={{ margin: 0, color: 'rgba(255,255,255,0.8)', fontSize: 13, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{indieOfDay.description}</p>
                                    )}
                                  </div>
                                  <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <div style={{ background: 'rgba(255,255,255,0.06)', padding: '6px 10px', borderRadius: 10, fontSize: 13 }}>👤 {indieOfDay.userId?.username || 'Unknown'}</div>
                                    <div style={{ marginLeft: 'auto', color: '#64C8FF', fontWeight: 700 }}>View</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* What's New header */}
          <div style={{ margin: '18px 0 8px 0' }}>
            <h3 style={{ margin: 0, color: '#64C8FF' }}>What's New</h3>
          </div>

          <div className="activity-grid">
            {activityItems.map((item, index) => (
              <div 
                key={index} 
                className={`activity-item ${item.gradient}`}
                onClick={() => handleNavigate(item.path)}
              >
                <div className="activity-item-icon">{item.icon}</div>
                <div className="activity-item-content">
                  <div className="activity-item-count">{item.count}</div>
                  <div className="activity-item-label">
                    {item.count === 1 ? item.label : item.labelPlural}
                  </div>
                </div>
                <div className="activity-item-arrow">→</div>
              </div>
            ))}
          </div>

          <div className="activity-modal-footer">
            <p className="activity-footer-text">
              Click on any item to explore, or close this to continue browsing.
            </p>
            <p className="activity-footer-note">
              This notification appears once every 24 hours
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityModal;
